import { useMemo, useState, useRef, useEffect, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays, Check, Clock } from 'lucide-react';
import { getDiaSemanaFromDate } from '../../api/hooks/useHorarios';
import { useProfessorGradeSemanal } from './useProfessorGradeSemanal';
import { useChamadas } from '../../api/hooks/useChamadas';
import { formatTurmaDisplay } from './ChamadaProfessorDesktop';
import type { HorarioAula } from '../../types/models';

const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];
const WEEK_HEADERS = [
  { short: 'SEG', full: 'SEGUNDA' },
  { short: 'TER', full: 'TERÇA' },
  { short: 'QUA', full: 'QUARTA' },
  { short: 'QUI', full: 'QUINTA' },
  { short: 'SEX', full: 'SEXTA' },
  { short: 'SÁB', full: 'SÁBADO' },
  { short: 'DOM', full: 'DOMINGO' },
];

function toIso(y: number, m: number, d: number) {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function mesmoDia(a: Date, iso: string) {
  const b = new Date(iso + 'T12:00:00');
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function parseHojeIso(hojeIso?: string): { date: Date; iso: string } {
  if (hojeIso && /^\d{4}-\d{2}-\d{2}$/.test(hojeIso)) {
    return { date: new Date(`${hojeIso}T12:00:00`), iso: hojeIso };
  }
  const d = new Date();
  return {
    date: d,
    iso: toIso(d.getFullYear(), d.getMonth(), d.getDate()),
  };
}

interface Props {
  professorId: string;
  statusMap?: Record<string, string>;
  onIrChamada?: () => void;
  /** Sincroniza dia com página de chamada */
  selectedIso?: string;
  onSelectIso?: (iso: string) => void;
  /**
   * Dia de referência (“Hoje”) — use o dia da simulação quando houver.
   * Sem isso, o calendário marca o dia civil do browser.
   */
  hojeIso?: string;
  /** Grade + painel à direita (mesmo card, estilo dashboard) */
  pickerOnly?: boolean;
  /** Conteúdo do painel do dia (ex.: movimento de chamadas) */
  dayPanel?: ReactNode;
  className?: string;
  /** Contagens por dia (ex.: diários); se omitido, usa grade semanal */
  countByDay?: Record<string, number>;
  /** Sumário de chamadas por dia { feitas, pendentes, total } */
  chamadasSummaryByDay?: Record<string, { feitas: number; pendentes: number; total: number }>;
  hideLegend?: boolean;
  onGoHoje?: () => void;
  calendarTitle?: ReactNode;
  calendarSubtitle?: ReactNode;
  countUnit?: { one: string; many: string };
  legendVariant?: 'grade' | 'diario' | 'escola';
  /** Controle externo do mês exibido (ex.: dashboard escola) */
  viewYear?: number;
  viewMonth?: number;
  onViewChange?: (year: number, month: number) => void;
}

export function DesktopMonthCalendar({
  professorId,
  statusMap = {},
  onIrChamada,
  selectedIso: selectedIsoProp,
  onSelectIso,
  hojeIso: hojeIsoProp,
  pickerOnly = false,
  dayPanel,
  className = '',
  countByDay,
  chamadasSummaryByDay,
  calendarTitle = 'Calendário',
  calendarSubtitle,
  hideLegend = false,
  onGoHoje,
  countUnit = { one: 'aula', many: 'aulas' },
  legendVariant = 'grade',
  viewYear: viewYearProp,
  viewMonth: viewMonthProp,
  onViewChange,
}: Props) {
  const { date: hojeRef, iso: hojeIso } = useMemo(
    () => parseHojeIso(hojeIsoProp),
    [hojeIsoProp],
  );
  const ontemIso = useMemo(() => {
    const d = new Date(hojeRef);
    d.setDate(d.getDate() - 1);
    return toIso(d.getFullYear(), d.getMonth(), d.getDate());
  }, [hojeRef]);
  const amanhaIso = useMemo(() => {
    const d = new Date(hojeRef);
    d.setDate(d.getDate() + 1);
    return toIso(d.getFullYear(), d.getMonth(), d.getDate());
  }, [hojeRef]);
  const [viewYearInternal, setViewYearInternal] = useState(hojeRef.getFullYear());
  const [viewMonthInternal, setViewMonthInternal] = useState(hojeRef.getMonth());
  const viewYear = viewYearProp ?? viewYearInternal;
  const viewMonth = viewMonthProp ?? viewMonthInternal;
  const setViewYear = (y: number) => {
    if (onViewChange) onViewChange(y, viewMonth);
    else setViewYearInternal(y);
  };
  const setViewMonth = (m: number) => {
    if (onViewChange) onViewChange(viewYear, m);
    else setViewMonthInternal(m);
  };
  const [selectedIsoInternal, setSelectedIsoInternal] = useState(hojeIso);
  const selectedIso = selectedIsoProp ?? selectedIsoInternal;
  const [detailPulse, setDetailPulse] = useState(false);
  const detailRef = useRef<HTMLDivElement>(null);
  const anchoredHojeRef = useRef(hojeIso);

  const { totalAulasSemana, isLoading, aulasNoDia, contagemNoDia } =
    useProfessorGradeSemanal(professorId);

  const monthRange = useMemo(() => {
    const start = new Date(viewYear, viewMonth - 1, 1);
    const end = new Date(viewYear, viewMonth + 2, 0);
    return {
      dataInicio: toIso(start.getFullYear(), start.getMonth(), start.getDate()),
      dataFim: toIso(end.getFullYear(), end.getMonth(), end.getDate()),
    };
  }, [viewYear, viewMonth]);

  const { chamadas: chamadasDoMes } = useChamadas({
    dataInicio: monthRange.dataInicio,
    dataFim: monthRange.dataFim,
    professorId,
    fetchAll: true,
    enabled: !!professorId,
    refetchIntervalMs: 5000,
  });

  const chamadasSummaryFromQuery = useMemo(() => {
    if (!chamadasDoMes || !Array.isArray(chamadasDoMes)) return {};
    const map: Record<string, { feitas: number; pendentes: number; total: number }> = {};
    const seenSlotsByDay: Record<string, Set<string>> = {};

    for (const ch of chamadasDoMes) {
      const dateVal = ch.dataReferencia || ch.data || ch.criadoEm;
      if (!dateVal) continue;
      let d = '';
      const rawVal: unknown = dateVal;
      if (typeof rawVal === 'string') {
        const trimmed = rawVal.trim();
        if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
          // Extrai diretamente YYYY-MM-DD civil para blindar contra fuso horário (GMT-3)
          d = trimmed.slice(0, 10);
        } else {
          const parsed = new Date(trimmed);
          if (!isNaN(parsed.getTime())) {
            d = toIso(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
          }
        }
      } else if (rawVal instanceof Date && !isNaN(rawVal.getTime())) {
        d = toIso(rawVal.getFullYear(), rawVal.getMonth(), rawVal.getDate());
      }
      if (!d) continue;
      if (!map[d]) {
        const total = countByDay ? (countByDay[d] ?? 0) : contagemNoDia(d);
        map[d] = { feitas: 0, pendentes: total, total };
        seenSlotsByDay[d] = new Set<string>();
      }
      const statusUpper = String(ch.status || '').toUpperCase();
      const isConcluida =
        statusUpper === 'CONCLUIDA' ||
        (statusUpper === 'ABERTA' && ((ch as any).totalAlunos ?? 0) > 0);
      if (isConcluida) {
        const slotKey =
          ch.horarioAulaId ||
          (ch.turmaId ? `${ch.turmaId}:${ch.materiaId ?? ''}:${ch.numeroAula ?? ''}` : ch.id);
        if (!seenSlotsByDay[d].has(slotKey)) {
          seenSlotsByDay[d].add(slotKey);
          map[d].feitas += 1;
        }
      }
    }
    for (const d of Object.keys(map)) {
      map[d].pendentes = Math.max(0, map[d].total - map[d].feitas);
    }
    return map;
  }, [chamadasDoMes, countByDay, contagemNoDia]);

  // Quando o dia de referência muda (ex.: loop da simulação), ancora mês + seleção.
  useEffect(() => {
    if (!hojeIsoProp || !/^\d{4}-\d{2}-\d{2}$/.test(hojeIsoProp)) return;
    if (anchoredHojeRef.current === hojeIsoProp) return;
    anchoredHojeRef.current = hojeIsoProp;
    const [y, m] = hojeIsoProp.split('-').map(Number);
    if (y && m) {
      if (onViewChange) onViewChange(y, m - 1);
      else {
        setViewYearInternal(y);
        setViewMonthInternal(m - 1);
      }
    }
    if (!selectedIsoProp) setSelectedIsoInternal(hojeIsoProp);
  }, [hojeIsoProp, onViewChange, selectedIsoProp]);

  const cells = useMemo(() => {
    const first = new Date(viewYear, viewMonth, 1);
    const lastDay = new Date(viewYear, viewMonth + 1, 0).getDate();
    let startPad = first.getDay() - 1;
    if (startPad < 0) startPad = 6;

    const prevMonthLastDay = new Date(viewYear, viewMonth, 0).getDate();
    const prevMonthDate = new Date(viewYear, viewMonth - 1, 1);
    const prevY = prevMonthDate.getFullYear();
    const prevM = prevMonthDate.getMonth();

    const grid: { iso: string; day: number; inMonth: boolean }[] = [];
    for (let i = startPad - 1; i >= 0; i--) {
      const d = prevMonthLastDay - i;
      grid.push({
        iso: toIso(prevY, prevM, d),
        day: d,
        inMonth: false,
      });
    }

    for (let d = 1; d <= lastDay; d++) {
      grid.push({
        iso: toIso(viewYear, viewMonth, d),
        day: d,
        inMonth: true,
      });
    }

    const remaining = (7 - (grid.length % 7)) % 7;
    if (remaining > 0) {
      const nextMonthDate = new Date(viewYear, viewMonth + 1, 1);
      const nextY = nextMonthDate.getFullYear();
      const nextM = nextMonthDate.getMonth();
      for (let d = 1; d <= remaining; d++) {
        grid.push({
          iso: toIso(nextY, nextM, d),
          day: d,
          inMonth: false,
        });
      }
    }

    return grid;
  }, [viewYear, viewMonth]);

  const dayCount = (iso: string) =>
    countByDay ? (countByDay[iso] ?? 0) : contagemNoDia(iso);

  const selectedAulas = aulasNoDia(selectedIso);

  const turnosSelectedAulas = useMemo(() => {
    const grupos: {
      key: string;
      label: string;
      horaInicio: string;
      horaFim: string;
      aulas: HorarioAula[];
    }[] = [];

    const ordenadas = [...selectedAulas].sort((a, b) =>
      (a.horaInicio || '').localeCompare(b.horaInicio || '')
    );

    ordenadas.forEach((aula) => {
      let tKey = (aula.turma as any)?.turno || '';
      let tLabel = '';
      if (tKey === 'MANHA') tLabel = 'Manhã';
      else if (tKey === 'TARDE') tLabel = 'Tarde';
      else if (tKey === 'NOITE') tLabel = 'Noite';
      else if (tKey === 'INTEGRAL') tLabel = 'Integral';
      else {
        const h = parseInt((aula.horaInicio || '').split(':')[0], 10);
        if (h < 12) {
          tKey = 'MANHA';
          tLabel = 'Manhã';
        } else if (h < 18) {
          tKey = 'TARDE';
          tLabel = 'Tarde';
        } else {
          tKey = 'NOITE';
          tLabel = 'Noite';
        }
      }

      let g = grupos.find((x) => x.key === tKey);
      if (!g) {
        g = {
          key: tKey,
          label: tLabel,
          horaInicio: aula.horaInicio || '',
          horaFim: aula.horaFim || '',
          aulas: [],
        };
        grupos.push(g);
      }
      g.aulas.push(aula);
      if (aula.horaInicio && (!g.horaInicio || aula.horaInicio < g.horaInicio)) g.horaInicio = aula.horaInicio;
      if (aula.horaFim && (!g.horaFim || aula.horaFim > g.horaFim)) g.horaFim = aula.horaFim;
    });

    return grupos;
  }, [selectedAulas]);

  const selectedCount = dayCount(selectedIso);
  const resolvedSubtitle =
    calendarSubtitle ??
    (isLoading
      ? 'Carregando…'
      : countByDay
        ? `${Object.values(countByDay).reduce((a, b) => a + b, 0)} registro${Object.values(countByDay).reduce((a, b) => a + b, 0) !== 1 ? 's' : ''} no período`
        : `${totalAulasSemana} aula${totalAulasSemana !== 1 ? 's' : ''}/semana (grade)`);
  const isTodaySelected = mesmoDia(hojeRef, selectedIso);
  const diaSemanaSel = getDiaSemanaFromDate(selectedIso);
  const isDomingo = diaSemanaSel === null;

  const selectDay = (iso: string) => {
    if (onSelectIso) onSelectIso(iso);
    else setSelectedIsoInternal(iso);
    setDetailPulse(true);
    if (!pickerOnly) {
      requestAnimationFrame(() => {
        detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }
  };

  useEffect(() => {
    if (!selectedIsoProp) return;
    const [y, m] = selectedIsoProp.split('-').map(Number);
    if (y && m) {
      const mo = m - 1;
      if (onViewChange) onViewChange(y, mo);
      else {
        setViewYearInternal(y);
        setViewMonthInternal(mo);
      }
    }
  }, [selectedIsoProp, onViewChange]);

  useEffect(() => {
    if (!detailPulse) return;
    const t = window.setTimeout(() => setDetailPulse(false), 400);
    return () => window.clearTimeout(t);
  }, [detailPulse, selectedIso]);

  const navMonth = (delta: number) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const goHoje = () => {
    setViewYear(hojeRef.getFullYear());
    setViewMonth(hojeRef.getMonth());
    selectDay(hojeIso);
  };

  const calClass = [
    'dash-desk-cal',
    pickerOnly ? 'dash-desk-cal--picker' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={calClass} aria-label="Calendário pedagógico">
      <div className="dash-desk-cal-inner">
        <div className="dash-desk-cal-head">
          <div className="dash-desk-cal-title">
            <span className="dash-desk-cal-title-icon" aria-hidden>
              <CalendarDays size={18} strokeWidth={2.25} style={{ color: '#2563eb' }} />
            </span>
            <div className="dash-desk-cal-title-text">
              <h3>{calendarTitle}</h3>
              {calendarSubtitle !== undefined ? (
                calendarSubtitle && (
                  <span className="dash-desk-cal-subtitle" aria-live="polite">
                    {calendarSubtitle}
                  </span>
                )
              ) : (
                resolvedSubtitle && (
                  <span className="dash-desk-cal-subtitle" aria-live="polite">
                    {resolvedSubtitle}
                  </span>
                )
              )}
            </div>
          </div>

          <div className="dash-desk-cal-nav">
            <button type="button" className="dash-desk-action-btn dash-desk-cal-nav-btn" onClick={() => navMonth(-1)} aria-label="Mês anterior">
              <ChevronLeft size={16} />
            </button>
            <span className="dash-desk-cal-month-label">
              {MESES[viewMonth]} {viewYear}
            </span>
            <button type="button" className="dash-desk-action-btn dash-desk-cal-nav-btn" onClick={() => navMonth(1)} aria-label="Próximo mês">
              <ChevronRight size={16} />
            </button>
            <button
              type="button"
              className="dash-desk-action-btn primary dash-desk-cal-btn-hoje-head"
              onClick={() => {
                if (onGoHoje) onGoHoje();
                else goHoje();
              }}
            >
              HOJE
            </button>
          </div>

          {!hideLegend && (
            <div className="dash-desk-cal-legend" role="list" aria-label="Legenda do calendário">
              <span role="listitem"><i className="dot dot-today" /> Hoje</span>
              {legendVariant === 'diario' ? (
                <>
                  <span role="listitem"><i className="dot dot-class" /> Com registro</span>
                  <span role="listitem"><i className="dot dot-free" /> Sem registro</span>
                </>
              ) : legendVariant === 'escola' ? (
                <>
                  <span role="listitem"><i className="dot dot-class" /> Com dados no dia</span>
                  <span role="listitem"><i className="dot dot-free" /> Sem dados</span>
                </>
              ) : (
                <>
                  <span role="listitem"><i className="dot dot-class" /> Com aulas na grade</span>
                  <span role="listitem"><i className="dot dot-free" /> Sem aula</span>
                </>
              )}
            </div>
          )}
        </div>

        <div className={`dash-desk-cal-body${pickerOnly ? ' dash-desk-cal-body--picker' : ''}`}>
          <div className="dash-desk-cal-grid-wrap">
            <div className="dash-desk-cal-grid">
              {WEEK_HEADERS.map((h) => (
                <div key={h.short} className="dash-desk-cal-dow">
                  <span className="dash-desk-cal-dow-full">{h.full}</span>
                  <span className="dash-desk-cal-dow-short">{h.short}</span>
                </div>
              ))}
              {cells.map((cell, idx) => {
                if (!cell.iso) {
                  return <div key={`pad-${idx}`} className="dash-desk-cal-cell dash-desk-cal-cell--pad" />;
                }
                const count = dayCount(cell.iso);
                const isToday = mesmoDia(hojeRef, cell.iso);
                const isOntem = cell.iso === ontemIso;
                const isAmanha = cell.iso === amanhaIso;
                const dayLabel = isToday
                  ? 'HOJE'
                  : isOntem
                  ? 'ONTEM'
                  : isAmanha
                  ? 'AMANHÃ'
                  : `DIA ${cell.day}`;

                const isSelected = cell.iso === selectedIso;
                const isSun = getDiaSemanaFromDate(cell.iso) === null || (cell.iso ? new Date(cell.iso + 'T12:00:00').getDay() === 0 : false);
                const isSat = cell.iso ? new Date(cell.iso + 'T12:00:00').getDay() === 6 : false;
                const isWeekend = isSun || isSat;
                const isPast = !!cell.iso && cell.iso < hojeIso;
                const isFuture = !!cell.iso && cell.iso > hojeIso;
                const stats = (cell.iso ? chamadasSummaryByDay?.[cell.iso] : undefined) || (cell.iso ? chamadasSummaryFromQuery[cell.iso] : undefined);
                const hasStats = !!stats;
                const feitas = stats ? stats.feitas : 0;
                const totalChamadas = stats ? stats.total : count;
                const pendentes = stats ? stats.pendentes : Math.max(0, totalChamadas - feitas);

                return (
                  <button
                    key={cell.iso}
                    type="button"
                    className={[
                      'dash-desk-cal-cell',
                      !cell.inMonth ? 'out-of-month' : '',
                      isToday ? 'today' : '',
                      isSelected ? 'selected' : '',
                      count > 0 ? 'has-class' : '',
                      isSun ? 'sunday' : '',
                      isSat ? 'saturday' : '',
                      isWeekend ? 'weekend' : '',
                      isPast ? 'past' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => selectDay(cell.iso!)}
                    aria-pressed={isSelected}
                    aria-label={`Dia ${cell.day} de ${MESES[viewMonth]}, ${count} ${count === 1 ? countUnit.one : countUnit.many}`}
                  >
                    <span className="dash-desk-cal-day-num">{dayLabel}</span>
                    {count > 0 ? (
                      <div className="dash-desk-cal-badge-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, width: '100%', flexWrap: 'nowrap', marginTop: 'auto' }}>
                        {isFuture ? (
                          <span
                            className="dash-desk-cal-badge dash-desk-cal-badge--default"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 2,
                              width: '100%',
                              padding: '3px 5px',
                              borderRadius: 5,
                              background: '#f1f5f9',
                              color: '#64748b',
                              border: '1px solid #e2e8f0',
                              fontSize: '0.62rem',
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                              lineHeight: 1.1,
                              letterSpacing: '0.1px',
                            }}
                          >
                            {count} {count === 1 ? 'CHAMADA' : 'CHAMADAS'}
                          </span>
                        ) : (isPast || isToday || hasStats) ? (
                          <>
                            {feitas > 0 && (
                              <span
                                className="dash-desk-cal-badge dash-desk-cal-badge--done"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 2,
                                  padding: '3px 5px',
                                  borderRadius: 5,
                                  background: '#10b981',
                                  color: '#ffffff',
                                  fontSize: '0.62rem',
                                  fontWeight: 600,
                                  whiteSpace: 'nowrap',
                                  lineHeight: 1.1,
                                  boxShadow: '0 1px 3px rgba(16,185,129,0.25)',
                                  letterSpacing: '0.1px',
                                }}
                              >
                                <Check size={11} strokeWidth={2.5} />
                                {pendentes === 0 ? `${feitas} FEITAS` : `${feitas}`}
                              </span>
                            )}
                            {pendentes > 0 && (
                              <span
                                className="dash-desk-cal-badge dash-desk-cal-badge--waiting"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 2,
                                  width: feitas === 0 ? '100%' : 'auto',
                                  padding: '3px 5px',
                                  borderRadius: 5,
                                  background: '#f59e0b',
                                  color: '#ffffff',
                                  fontSize: feitas === 0 ? '0.62rem' : '0.58rem',
                                  fontWeight: 600,
                                  whiteSpace: 'nowrap',
                                  lineHeight: 1.1,
                                  boxShadow: '0 1px 3px rgba(245,158,11,0.25)',
                                  letterSpacing: '0.1px',
                                }}
                              >
                                <Clock size={10} strokeWidth={2} />
                                {pendentes} RESTAM
                              </span>
                            )}
                            {feitas === 0 && pendentes === 0 && (
                              <span
                                className="dash-desk-cal-badge dash-desk-cal-badge--default"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 2,
                                  width: '100%',
                                  padding: '3px 5px',
                                  borderRadius: 5,
                                  background: '#f1f5f9',
                                  color: '#64748b',
                                  border: '1px solid #e2e8f0',
                                  fontSize: '0.62rem',
                                  fontWeight: 600,
                                  whiteSpace: 'nowrap',
                                  lineHeight: 1.1,
                                  letterSpacing: '0.1px',
                                }}
                              >
                                {count} {count === 1 ? 'CHAMADA' : 'CHAMADAS'}
                              </span>
                            )}
                          </>
                        ) : (
                          <span
                            className="dash-desk-cal-badge dash-desk-cal-badge--default"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 2,
                              width: '100%',
                              padding: '3px 5px',
                              borderRadius: 5,
                              background: isToday ? '#2563eb' : '#f1f5f9',
                              color: isToday ? '#ffffff' : '#64748b',
                              border: isToday ? 'none' : '1px solid #e2e8f0',
                              fontSize: '0.62rem',
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                              lineHeight: 1.1,
                              boxShadow: isToday ? '0 1px 3px rgba(37,99,235,0.25)' : 'none',
                              letterSpacing: '0.1px',
                            }}
                          >
                            {count} {count === 1 ? 'CHAMADA' : 'CHAMADAS'}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="dash-desk-cal-load dash-desk-cal-load--empty" aria-hidden />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {pickerOnly && dayPanel && (
            <div className="dash-desk-cal-detail dash-desk-cal-detail--slot" aria-live="polite">
              {dayPanel}
            </div>
          )}

          {!pickerOnly && (
          <div
            ref={detailRef}
            className={`dash-desk-cal-detail${detailPulse ? ' dash-desk-cal-detail--pulse' : ''}`}
            aria-live="polite"
            key={selectedIso}
          >
            <p className="dash-desk-cal-detail-label">Dia selecionado</p>
            <div className="dash-desk-cal-detail-head">
              <strong>
                {new Date(selectedIso + 'T12:00:00').toLocaleDateString('pt-BR', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short',
                })}
              </strong>
              <span className="dash-desk-cal-detail-meta">
                {isDomingo
                  ? 'Domingo — sem grade'
                  : selectedCount === 0
                    ? 'Sem aulas na grade'
                    : `${selectedCount} aula(s) na grade`}
              </span>
            </div>

            {isDomingo ? (
              <p className="dash-desk-cal-detail-empty">Domingo não entra na grade semanal.</p>
            ) : selectedCount === 0 ? (
              <p className="dash-desk-cal-detail-empty">
                Nenhuma aula neste dia da semana. Dias com número roxo na grade têm aulas (Ter, Qua, etc.).
              </p>
            ) : (
              <div className="dash-desk-cal-turnos-wrap" style={{ display: 'flex', flexDirection: 'column', gap: 6, overflowY: 'auto' }}>
                {turnosSelectedAulas.map((grupo) => (
                  <div key={grupo.key} className="cham-desk-turno-section">
                    <div className="cham-desk-turno-header">
                      <span className="cham-desk-turno-time">{grupo.horaInicio}</span>
                      <span className="cham-desk-turno-title">TURNO {grupo.label.toUpperCase()}</span>
                      <span className="cham-desk-turno-time">{grupo.horaFim}</span>
                    </div>
                    <ul className="dash-desk-cal-detail-list">
                      {grupo.aulas.map((h: HorarioAula) => {
                        const done = isTodaySelected && statusMap[h.id] === 'CONCLUIDA';
                        return (
                          <li key={h.id}>
                            <time>
                              {h.horaInicio}–{h.horaFim}
                            </time>
                            <span className="dash-desk-cal-detail-materia">
                              {(h.materia?.nome || `${h.numeroAula}ª Aula`).toUpperCase()}
                            </span>
                            <span className="dash-desk-cal-detail-turma">{formatTurmaDisplay(h.turma)}</span>
                            {isTodaySelected && (
                              <span className={`dash-desk-cal-detail-status ${done ? 'ok' : 'pend'}`}>
                                {done ? 'Chamada OK' : 'Pendente'}
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {isTodaySelected && selectedCount > 0 && onIrChamada && (
              <button
                type="button"
                className="dash-desk-action-btn primary dash-desk-cal-detail-cta"
                onClick={onIrChamada}
              >
                Ir para chamada
              </button>
            )}
          </div>
          )}
        </div>
      </div>
    </section>
  );
}
